if (!customElements.get('product-form')) {
  let cartAddQueue = Promise.resolve();

  /**
   * Runs cart additions one at a time across every product form on the page.
   * This prevents two neighbouring product cards from updating and re-rendering
   * the cart drawer at the same time.
   * @param {Function} task - Async cart-add task.
   * @returns {Promise}
   */
  const enqueueCartAdd = (task) => {
    const queuedTask = cartAddQueue
      .catch(() => {
        // A failed earlier request must not block later cart additions.
      })
      .then(task);

    cartAddQueue = queuedTask.catch(() => {
      // Keep the shared queue usable after a failed task.
    });

    return queuedTask;
  };

  class ProductForm extends HTMLElement {
    constructor() {
      super();
      this.form = null;
      this.boundForm = null;
      this.cartDrawer = null;
      this.initFrame = null;
      this.submitHandler = this.handleSubmit.bind(this);
    }

    connectedCallback() {
      if (this.initFrame) window.cancelAnimationFrame(this.initFrame);
      this.initFrame = window.requestAnimationFrame(() => this.init());
    }

    disconnectedCallback() {
      if (this.initFrame) window.cancelAnimationFrame(this.initFrame);
      this.initFrame = null;

      if (this.boundForm) {
        this.boundForm.removeEventListener('submit', this.submitHandler);
        this.boundForm = null;
      }
    }

    init() {
      this.initFrame = null;
      if (!this.isConnected) return;

      const form = this.querySelector('.js-product-form');
      if (!form) return;

      if (this.boundForm && this.boundForm !== form) {
        this.boundForm.removeEventListener('submit', this.submitHandler);
        this.boundForm = null;
      }

      this.form = form;
      this.cartDrawer = document.querySelector('cart-drawer');

      // Shopify's accelerated checkout "More payment options" control can
      // throw an empty-URL parsing error inside Meta's embedded browsers
      // (Facebook, Instagram, Messenger). In those webviews, keep the normal
      // Add to Cart path available and remove only the unsupported accelerated
      // checkout UI. Standard Safari, Chrome, Edge, and other browsers are
      // unaffected.
      this.applyMetaInAppCheckoutFallback();

      if (theme.settings.afterAtc !== 'no-js' && this.boundForm !== this.form) {
        this.form.addEventListener('submit', this.submitHandler);
        this.boundForm = this.form;
      }
    }

    /**
     * Detects Meta-owned embedded browsers using their user-agent tokens.
     * @returns {boolean}
     */
    isMetaInAppBrowser() {
      const userAgent = window.navigator.userAgent || '';
      return /FBAN|FBAV|FB_IAB|FB4A|FBIOS|Instagram|MessengerForiOS|Threads/i.test(userAgent);
    }

    /**
     * Falls back to the standard product form in Meta embedded browsers.
     * Shopify generates the accelerated checkout internals, so the theme cannot
     * safely repair the failing URL inside that component. Hiding the component
     * prevents the dead click while preserving Add to Cart and normal checkout.
     */
    applyMetaInAppCheckoutFallback() {
      if (!this.isMetaInAppBrowser()) return;

      const dynamicCheckout = this.querySelector('.product-info__dynamic-checkout-button');
      if (!dynamicCheckout) return;

      dynamicCheckout.hidden = true;
      dynamicCheckout.style.display = 'none';
      dynamicCheckout.setAttribute('aria-hidden', 'true');
      dynamicCheckout.dataset.qlsDisabledReason = 'meta-in-app-browser';

      const addToCartButton = this.querySelector('[name="add"]');
      if (addToCartButton) {
        addToCartButton.classList.remove('btn--secondary');
        addToCartButton.classList.add('btn--primary');
      }

      const addToCartRow = this.querySelector('.product-info__add-to-cart');
      if (addToCartRow) addToCartRow.classList.remove('qty-input--secondary-btn');
    }

    /**
     * Handles submission of the product form.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      if (evt.target.id === 'product-signup_form') return;

      evt.preventDefault();

      const form = evt.currentTarget || this.form;
      const submitBtn = this.querySelector('[name="add"]');

      // A dynamically replaced or incomplete product card should fail safely.
      if (!form || !submitBtn) return;
      if (submitBtn.getAttribute('aria-disabled') === 'true') return;

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.errorMsg = null;
      this.setErrorMsgState();

      // Lock this button immediately. Other product-card submissions can still
      // be captured, but their requests are queued until this one has finished.
      submitBtn.setAttribute('aria-disabled', 'true');
      submitBtn.setAttribute('aria-busy', 'true');
      submitBtn.classList.add('is-loading');

      const formData = new FormData(form);
      let sections = 'cart-icon-bubble';
      const cartDrawerSection = this.cartDrawer && this.cartDrawer.closest('.shopify-section');
      if (cartDrawerSection) {
        sections += `,${cartDrawerSection.id.replace('shopify-section-', '')}`;
      }

      formData.append('sections_url', window.location.pathname);
      formData.append('sections', sections);

      let succeeded = false;

      try {
        succeeded = await enqueueCartAdd(() => this.submitProduct(formData));
      } finally {
        if (succeeded) submitBtn.classList.add('is-success');

        submitBtn.removeAttribute('aria-disabled');
        submitBtn.removeAttribute('aria-busy');

        setTimeout(() => {
          submitBtn.classList.remove('is-loading');
          submitBtn.classList.remove('is-success');
        }, 1400);
      }
    }

    /**
     * Performs one serialized product addition and waits for the cart drawer to
     * finish rendering before the next queued addition is allowed to start.
     * @param {FormData} formData - Captured product form data.
     * @returns {Promise<boolean>}
     */
    async submitProduct(formData) {
      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      };

      /*
       * QLS performance path:
       * - Start the old-cart snapshot without blocking the actual add request.
       * - Begin loading first-use cart-drawer dependencies at the same time.
       */
      const oldCartPromise = fetch(`${theme.routes.cart}.js`, {
        headers: { Accept: 'application/json' },
        cache: 'no-store'
      })
        .then((cartResponse) => (cartResponse.ok ? cartResponse.json() : null))
        .catch(() => null);

      if (this.cartDrawer && typeof this.cartDrawer.prepareForRender === 'function') {
        this.cartDrawer.prepareForRender().catch(() => {
          // renderContents will retry if an early dependency load fails.
        });
      }

      try {
        const response = await fetch(theme.routes.cartAdd, fetchRequestOpts);
        const data = await response.json();
        let error = typeof data.description === 'string' ? data.description : data.message;
        if (data.errors && typeof data.errors === 'object') {
          error = Object.entries(data.errors).map((item) => item[1].join(', '));
        }

        if (data.status) this.setErrorMsgState(error);
        if (!response.ok) throw new Error(response.status);

        document.dispatchEvent(new CustomEvent('qls:cart-item-touched', {
          detail: {
            itemKey: data.key || '',
            variantId: data.variant_id,
            quantity: data.quantity,
            source: 'add'
          }
        }));

        if (theme.settings.afterAtc === 'page') {
          // Keep the existing short success tick before a full-page redirect.
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        } else {
          // Update the cart count as soon as Shopify confirms the add.
          ProductForm.updateCartIcon(data);

          // If item was added from Quick Add drawer, show "Added to cart" message.
          const quickAddDrawer = this.closest('quick-add-drawer');
          if (quickAddDrawer) quickAddDrawer.addedToCart();

          /*
           * Wait for the drawer HTML to be fully replaced before releasing the
           * global add queue. This is what prevents two card additions from
           * racing through the same cart-drawer DOM.
           */
          if (this.cartDrawer) {
            try {
              await this.cartDrawer.renderContents(
                data,
                !quickAddDrawer && theme.settings.afterAtc === 'drawer'
              );
            } catch (renderError) {
              console.log(renderError); // eslint-disable-line
              await this.cartDrawer.refresh(true);
            }
          } else if (window.location.pathname === theme.routes.cart) {
            const cartItems = document.querySelector('cart-items');
            if (cartItems) {
              if (cartItems.dataset.empty === 'true') {
                window.location.reload();
              } else {
                await cartItems.refresh();
              }
            }
          }
        }

        /*
         * Preserve the existing on:cart:add / on:line-item:change events without
         * making the visible cart update wait for the old-cart snapshot.
         */
        oldCartPromise.then((oldCartData) => {
          const oldItems = oldCartData && Array.isArray(oldCartData.items)
            ? oldCartData.items
            : [];

          const itemInOldCart = oldItems.find(
            (item) => item.variant_id === data.variant_id
          );

          if (itemInOldCart) {
            this.dispatchEvent(new CustomEvent('on:line-item:change', {
              bubbles: true,
              detail: {
                variantId: data.variant_id,
                oldQuantity: itemInOldCart.quantity,
                newQuantity: (itemInOldCart.quantity === data.quantity)
                  ? itemInOldCart.quantity : data.quantity
              }
            }));
          } else {
            this.dispatchEvent(new CustomEvent('on:cart:add', {
              bubbles: true,
              detail: {
                variantId: data.variant_id
              }
            }));
          }
        });

        return true;
      } catch (error) {
        console.log(error); // eslint-disable-line
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg?.textContent
          }
        }));

        if (this.cartDrawer) {
          try {
            await this.cartDrawer.refresh(true);
          } catch (refreshError) {
            console.log(refreshError); // eslint-disable-line
          }
        }

        return false;
      }
    }

    /**
     * Updates the cart icon count in the header.
     * @param {object} response - Response JSON.
     */
    static updateCartIcon(response) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      const bubbleHTML = response && response.sections
        ? response.sections['cart-icon-bubble']
        : null;

      if (cartIconBubble && typeof bubbleHTML === 'string') {
        cartIconBubble.innerHTML = bubbleHTML;
      }
    }

    /**
     * Shows/hides an error message.
     * @param {string} [error=false] - Error to show a message for.
     */
    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
      if (!this.errorMsg) return;

      this.errorMsg.hidden = !error;
      if (error) {
        this.errorMsg.innerHTML = '';
        const errorArray = Array.isArray(error) ? error : [error];
        errorArray.forEach((err, index) => {
          if (index > 0) this.errorMsg.insertAdjacentHTML('beforeend', '<br>');
          this.errorMsg.insertAdjacentText('beforeend', err);
        });
      }
    }
  }

  customElements.define('product-form', ProductForm);
}
